import { useEffect, useState, useRef, useCallback } from 'react';
import { Globe, Palette, CreditCard, Image, Upload, Save, Calendar, Link2, Copy, Lock } from 'lucide-react';
import { toast } from 'sonner';
import errText from '../../lib/errText';
import { sportsApi } from '../SportsAuthContext';
import { useSportsAuth } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';


const inputStyle = {
  background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff',
  borderRadius: 8, padding: '10px 14px', width: '100%', outline: 'none',
  fontFamily: 'Manrope, sans-serif', fontSize: 14,
};

function SectionCard({ icon: Icon, title, children }) {
  return (
    <div className="rounded-xl p-6 mb-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
      <div className="flex items-center gap-2 mb-5">
        <span className="h-8 w-8 rounded-lg flex items-center justify-center" style={{ background: '#E6394618', color: '#E63946' }}>
          <Icon className="h-4 w-4" />
        </span>
        <h2 className="text-sm font-bold uppercase tracking-wide" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{title}</h2>
      </div>
      {children}
    </div>
  );
}

function FieldLabel({ children }) {
  return <span className="block text-xs uppercase tracking-wide mb-1.5" style={{ color: '#737373' }}>{children}</span>;
}

function ImageUploadField({ label, fieldKey, currentUrl, currentKey, onUpload }) {
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState(currentUrl);
  const ref = useRef();

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Upload via server proxy — bypasses R2 CORS restrictions
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', fieldKey);
      const res = await sportsApi.post('/sports/settings/upload-file', formData);
      setPreview(URL.createObjectURL(file));
      onUpload(fieldKey, res.data.key);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error('Upload failed. Try again.');
    } finally {
      setUploading(false);
      if (ref.current) ref.current.value = '';
    }
  };

  return (
    <div>
      <FieldLabel>{label}</FieldLabel>
      <div className="flex items-center gap-4">
        {preview ? (
          <img src={preview} alt={label} className="h-16 w-16 rounded-lg object-cover" style={{ border: '1px solid #2A2A2A' }} />
        ) : (
          <div className="h-16 w-16 rounded-lg flex items-center justify-center" style={{ background: '#1A1A1A', border: '1px dashed #2A2A2A' }}>
            <Image className="h-6 w-6" style={{ color: '#555' }} />
          </div>
        )}
        <button onClick={() => ref.current?.click()} disabled={uploading}
          className="flex items-center gap-2 px-4 py-2 rounded-md text-sm font-semibold transition-colors"
          style={{ background: '#2A2A2A', color: '#A3A3A3' }}>
          {uploading ? 'Uploading…' : <><Upload className="h-3.5 w-3.5" /> Upload</>}
        </button>
        <input ref={ref} type="file" accept="image/*" className="hidden" onChange={handleFile} />
      </div>
    </div>
  );
}

function ChangePasswordSection() {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!current) { toast.error('Enter your current password'); return; }
    if (next.length < 8) { toast.error('New password must be at least 8 characters'); return; }
    if (next !== confirm) { toast.error('New passwords do not match'); return; }
    setBusy(true);
    try {
      await sportsApi.post('/sports/auth/change-password', {
        current_password: current, new_password: next,
      });
      toast.success('Password changed. You stay signed in on this device.');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (e) {
      toast.error(errText(e, 'Could not change the password. Try again.'));
    } finally { setBusy(false); }
  };

  const filled = current && next && confirm;
  return (
    <SectionCard icon={Lock} title="Account Security">
      <div className="space-y-4" style={{ maxWidth: 420 }}>
        <div>
          <FieldLabel>Current password</FieldLabel>
          <input type="password" autoComplete="current-password" value={current}
            onChange={e => setCurrent(e.target.value)} style={inputStyle}
            data-testid="sports-settings-current-password" />
        </div>
        <div>
          <FieldLabel>New password</FieldLabel>
          <input type="password" autoComplete="new-password" value={next}
            onChange={e => setNext(e.target.value)} style={inputStyle}
            data-testid="sports-settings-new-password" />
          <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>At least 8 characters.</p>
        </div>
        <div>
          <FieldLabel>Confirm new password</FieldLabel>
          <input type="password" autoComplete="new-password" value={confirm}
            onChange={e => setConfirm(e.target.value)} style={inputStyle}
            data-testid="sports-settings-confirm-password" />
          {confirm && next !== confirm && (
            <p style={{ fontSize: 11, color: '#FF2D55', marginTop: 4 }}>Passwords do not match.</p>
          )}
        </div>
        <button onClick={submit} disabled={busy || !filled}
          data-testid="sports-settings-change-password-btn"
          className="px-5 py-2.5 rounded-md text-sm font-bold uppercase tracking-wide"
          style={{ background: filled ? '#E63946' : '#2A2A2A', color: filled ? '#fff' : '#737373',
            cursor: (busy || !filled) ? 'not-allowed' : 'pointer', opacity: busy ? 0.7 : 1 }}>
          {busy ? 'Updating…' : 'Update Password'}
        </button>
      </div>
    </SectionCard>
  );
}

function PayMongoSection({ settings, form, f, onChanged }) {
  const pm = settings.paymongo || {};
  const [secret, setSecret] = useState('');
  const [pub, setPub] = useState('');
  const [webhook, setWebhook] = useState('');
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showForm, setShowForm] = useState(false);

  const connect = async () => {
    if (!secret.trim()) { toast.error('Secret key is required'); return; }
    setBusy(true);
    try {
      const r = await sportsApi.post('/sports/settings/paymongo', {
        secret_key: secret.trim(), public_key: pub.trim(), webhook_secret: webhook.trim(),
      });
      toast.success(`PayMongo connected (${r.data.mode} mode)`);
      setSecret(''); setPub(''); setWebhook(''); setShowForm(false);
      onChanged();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not connect'); }
    finally { setBusy(false); }
  };

  const testConnection = async () => {
    setTesting(true);
    try {
      const r = await sportsApi.post('/sports/settings/paymongo/test');
      toast.success(`✓ Key still works — ${(r.data.mode || 'unknown').toUpperCase()} mode`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Test failed');
    } finally { setTesting(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await sportsApi.delete('/sports/settings/paymongo'); toast.success('PayMongo disconnected'); onChanged(); }
    catch { toast.error('Failed to disconnect'); }
    finally { setBusy(false); }
  };

  const arr = form.payment_amount_options || [];
  const toggleOpt = (id) => f('payment_amount_options')(arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id]);
  const methods = form.paymongo_methods || [];
  const toggleMethod = (id) => f('paymongo_methods')(methods.includes(id) ? methods.filter(x => x !== id) : [...methods, id]);
  const provider = form.payment_provider || 'gcash';
  const inS = { background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', borderRadius: 8, padding: '10px 12px', width: '100%', outline: 'none', fontSize: 14, boxSizing: 'border-box' };

  return (
    <div className="space-y-5">
      <p className="text-xs" style={{ color: '#555' }}>
        Choose ONE payment provider for your public booking page — only the selected one is shown to customers.
      </p>

      {/* Provider selector */}
      <div className="flex gap-2 flex-wrap">
        {[['gcash', 'GCash QR (manual proof)'], ['paymongo', 'PayMongo (online checkout)']].map(([id, lbl]) => (
          <button key={id} onClick={() => f('payment_provider')(id)} data-testid={`payprovider-${id}`}
            style={{ fontSize: 12, fontWeight: 800, padding: '9px 16px', borderRadius: 10, cursor: 'pointer',
              border: `1px solid ${provider === id ? '#00E676' : '#2A2A2A'}`, background: provider === id ? '#00E67618' : 'transparent', color: provider === id ? '#00E676' : '#a3a3a3' }}>
            {provider === id ? '✓ ' : ''}{lbl}
          </button>
        ))}
      </div>

      {provider === 'gcash' && (
        <p className="text-xs" style={{ color: '#737373' }}>
          Customers see your GCash name + QR and upload a payment screenshot for you to verify. Set your GCash name & QR image in the GCash section above.
        </p>
      )}

      {provider === 'paymongo' && (<>
      <p className="text-xs" style={{ color: '#555' }}>
        Connect the facility's OWN PayMongo account so online payments settle directly to you — the platform never holds your money. Your secret key is encrypted and never shown again.
      </p>

      {/* Connection status */}
      {pm.connected ? (
        <div style={{ background: '#00E67610', border: '1px solid #00E67640', borderRadius: 10, padding: 14 }}>
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <p style={{ fontSize: 13, fontWeight: 800, color: '#00E676' }} data-testid="paymongo-connected-status">
                ✓ Connected · {pm.mode?.toUpperCase()} mode
              </p>
              <p style={{ fontSize: 12, color: '#737373', marginTop: 2 }}>Secret {pm.secret_masked} · {pm.public_key || 'no public key'}{pm.webhook_set ? ' · webhook set' : ''}</p>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <button onClick={testConnection} disabled={testing || busy} data-testid="paymongo-test-btn"
                style={{ background: '#00BCD414', color: '#00BCD4', border: '1px solid #00BCD440', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: (testing || busy) ? 'not-allowed' : 'pointer', opacity: (testing || busy) ? 0.6 : 1 }}>
                {testing ? 'Testing…' : 'Test Connection'}
              </button>
              <button onClick={disconnect} disabled={busy} data-testid="paymongo-disconnect-btn"
                style={{ background: '#FF2D5514', color: '#FF2D55', border: '1px solid #FF2D5540', borderRadius: 8, padding: '8px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer' }}>Disconnect</button>
            </div>
          </div>
        </div>
      ) : (
        !showForm ? (
          <button onClick={() => setShowForm(true)} data-testid="paymongo-connect-open"
            style={{ background: '#00E676', color: '#0A0A0A', border: 'none', borderRadius: 10, padding: '11px 18px', fontSize: 13, fontWeight: 800, fontFamily: 'Oswald, sans-serif', textTransform: 'uppercase', cursor: 'pointer' }}>
            Connect PayMongo
          </button>
        ) : (
          <div className="space-y-3" style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', borderRadius: 10, padding: 14 }}>
            <div>
              <FieldLabel>Secret key (sk_live_… / sk_test_…) *</FieldLabel>
              <input type="password" value={secret} onChange={e => setSecret(e.target.value)} placeholder="sk_live_xxxxxxxx" style={inS} data-testid="paymongo-secret-input" />
            </div>
            <div>
              <FieldLabel>Public key (pk_…)</FieldLabel>
              <input value={pub} onChange={e => setPub(e.target.value)} placeholder="pk_live_xxxxxxxx" style={inS} data-testid="paymongo-public-input" />
            </div>
            <div>
              <FieldLabel>Webhook signing secret (optional)</FieldLabel>
              <input type="password" value={webhook} onChange={e => setWebhook(e.target.value)} placeholder="whsk_xxxxxxxx" style={inS} data-testid="paymongo-webhook-input" />
              <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Add a webhook in PayMongo pointing to <b style={{ color: '#a3a3a3' }}>{(process.env.REACT_APP_BACKEND_URL || '')}/api/sports/public/paymongo-webhook/{settings.organization_id || settings.org_id || '{your-org-id}'}</b>. Optional — payments still confirm via polling.</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => setShowForm(false)} style={{ flex: 1, background: 'transparent', border: '1px solid #2A2A2A', color: '#a3a3a3', borderRadius: 8, padding: 10, cursor: 'pointer' }}>Cancel</button>
              <button onClick={connect} disabled={busy} data-testid="paymongo-connect-submit"
                style={{ flex: 2, background: '#00E676', color: '#0A0A0A', border: 'none', borderRadius: 8, padding: 10, fontWeight: 800, cursor: 'pointer', opacity: busy ? 0.6 : 1 }}>
                {busy ? 'Validating…' : 'Validate & Connect'}
              </button>
            </div>
          </div>
        )
      )}

      {/* Payment options offered to customers */}
      <div>
        <FieldLabel>Payment options offered at checkout</FieldLabel>
        <div className="flex gap-2 flex-wrap mt-2">
          {[['full', 'Pay 100%'], ['deposit50', 'Pay 50% deposit'], ['counter', 'Pay at court']].map(([id, lbl]) => {
            // "Pay at court" contradicts "Pay First" mode — disable it in that case.
            const disabled = id === 'counter' && form.payment_mode === 'pay_first';
            return (
            <button key={id} onClick={() => !disabled && toggleOpt(id)} data-testid={`payopt-${id}`}
              disabled={disabled}
              title={disabled ? 'Pay at court is disabled while "Pay First" mode is active. Switch to "Pay on Arrival" to enable it.' : ''}
              style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: disabled ? 'not-allowed' : 'pointer',
                border: `1px solid ${arr.includes(id) && !disabled ? '#E63946' : '#2A2A2A'}`,
                background: arr.includes(id) && !disabled ? '#E6394618' : 'transparent',
                color: disabled ? '#3A3A3A' : (arr.includes(id) ? '#E63946' : '#a3a3a3'),
                opacity: disabled ? 0.5 : 1,
                textDecoration: disabled ? 'line-through' : 'none' }}>
              {arr.includes(id) && !disabled ? '✓ ' : ''}{lbl}
            </button>
            );
          })}
        </div>
        {form.payment_mode === 'pay_first' && arr.includes('counter') && (
          <p style={{ fontSize: 11, color: '#F59E0B', marginTop: 6 }}>
            "Pay at court" won't be shown to customers while <b>Pay First</b> mode is active — the two are mutually exclusive.
          </p>
        )}
      </div>

      {/* Methods */}
      <div>
        <FieldLabel>PayMongo methods</FieldLabel>
        <div className="flex gap-2 flex-wrap mt-2">
          {[['qrph', 'QR Ph'], ['gcash', 'GCash'], ['paymaya', 'Maya'], ['grab_pay', 'GrabPay'], ['card', 'Cards']].map(([id, lbl]) => (
            <button key={id} onClick={() => toggleMethod(id)} data-testid={`paymethod-${id}`}
              style={{ fontSize: 12, fontWeight: 700, padding: '7px 14px', borderRadius: 999, cursor: 'pointer',
                border: `1px solid ${methods.includes(id) ? '#E63946' : '#2A2A2A'}`, background: methods.includes(id) ? '#E6394618' : 'transparent', color: methods.includes(id) ? '#E63946' : '#a3a3a3' }}>
              {methods.includes(id) ? '✓ ' : ''}{lbl}
            </button>
          ))}
        </div>
        <p style={{ fontSize: 11, color: '#555', marginTop: 6 }}><b style={{ color: '#00E676' }}>QR Ph</b> works on any activated PayMongo account (no business permits needed) and is scannable by every PH bank / e-wallet app. GCash, Maya, GrabPay and Cards must each be enabled/approved in your PayMongo dashboard first.</p>
      </div>

      {/* Convenience fee */}
      <div>
        <FieldLabel>Convenience fee added to customer (%)</FieldLabel>
        <input type="number" step="0.5" min="0" max="20" value={form.payment_service_fee_percent}
          onChange={e => f('payment_service_fee_percent')(e.target.value === '' ? 0 : parseFloat(e.target.value))}
          style={{ ...inS, maxWidth: 160 }} data-testid="payment-fee-input" />
        <p style={{ fontSize: 11, color: '#555', marginTop: 4 }}>Optional. Added on top of the booking total at checkout and kept by you (helps offset PayMongo's own fees). Set 0 to charge customers nothing extra.</p>
      </div>
      </>)}
    </div>
  );
}


export default function SportsSettingsPage() {
  const { organization } = useSportsAuth();
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    site_mode: 'booking_only',
    theme_preset: 'Velocity Red',
    about: '',
    contact: '',
    location: '',
    gcash_name: '',
    gcash_qr_key: '',
    logo_key: '',
    hero_key: '',
    payment_mode: 'pay_first',
    timezone: 'Asia/Manila',
    play_now_enabled: true,
    play_now_buffer_min: 10,
    payment_provider: 'gcash',
    payment_amount_options: ['full', 'deposit50', 'counter'],
    payment_service_fee_percent: 0,
    paymongo_methods: ['qrph'],
    centralized_pos: false,
    eod_email_enabled: false,
  });
  const [gcashQrUrl, setGcashQrUrl] = useState(null);

  const BACKEND = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    sportsApi.get('/sports/settings').then(r => {
      const s = r.data || {};
      setSettings(s);
      setForm(f => ({ ...f, ...s }));
      setGcashQrUrl(s.gcash_qr_url || null);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const f = (key) => (val) => setForm(p => ({ ...p, [key]: val }));

  const handleImageUpload = (fieldKey, r2Key) => {
    setForm(p => ({ ...p, [`${fieldKey}_key`]: r2Key }));
    if (fieldKey === 'gcash_qr') {
      // Show local preview via the key - the URL will be refreshed on next settings load
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      // theme_preset / site_mode / logo_key are owned by Site Builder now —
      // never send them from here, or a stale form overwrites the theme.
      const { theme_preset, site_mode, logo_key, ...payload } = form;
      await sportsApi.put('/sports/settings', payload);
      toast.success('Settings saved!');
    } catch { toast.error('Failed to save settings'); }
    finally { setSaving(false); }
  };


  const slug = organization?.slug || '';
  const publicUrl = slug ? `${window.location.origin}/book/${slug}` : '';

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-3xl mx-auto px-5 py-10">
        <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Configuration</p>
        <h1 className="text-4xl uppercase tracking-tight mb-2" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>Setup My Page</h1>
        {publicUrl && (
          <a href={publicUrl} target="_blank" rel="noreferrer"
            className="inline-flex items-center gap-1.5 text-sm mb-8" style={{ color: '#E63946' }}>
            <Globe className="h-3.5 w-3.5" /> {publicUrl}
          </a>
        )}

        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3].map(i => <div key={i} className="h-32 rounded-xl animate-pulse" style={{ background: '#141414' }} />)}
          </div>
        ) : (
          <>
            {/* Theme & Appearance — MOVED to Site Builder (2026-08-18).
                Color theme, site mode and the facility logo style the PUBLIC
                site, so they live where the site is built
                (components/ThemeAppearance.jsx). Same pattern as the Custom
                Subdomain move below. save() strips the moved fields so a stale
                Settings form can never overwrite a theme picked over there. */}
            <SectionCard icon={Palette} title="Theme & Appearance">
              <p className="text-xs" style={{ color: '#555', lineHeight: 1.6 }}>
                Your color theme, site mode and facility logo are now managed in{' '}
                <a href="/sports/site-builder" style={{ color: '#E63946', fontWeight: 700 }}>Site → Theme &amp; Appearance</a>
                {' '}— everything that styles your public site, in one place.
              </p>
            </SectionCard>

            {/* GCash */}
            <SectionCard icon={CreditCard} title="GCash Payment Setup">
              <div className="space-y-4">
                <label>
                  <FieldLabel>Account Name (shown to customers)</FieldLabel>
                  <input value={form.gcash_name || ''} onChange={e => f('gcash_name')(e.target.value)}
                    placeholder="e.g. Juan Dela Cruz" style={inputStyle} data-testid="sports-settings-gcash-name" />
                </label>
                <ImageUploadField label="GCash QR Code Image" fieldKey="gcash_qr" currentUrl={gcashQrUrl} onUpload={handleImageUpload} />
                <p className="text-xs" style={{ color: '#555' }}>Upload your GCash QR screenshot. Customers will scan this to pay the booking deposit.</p>
              </div>
            </SectionCard>

            {/* Business Info */}
            <SectionCard icon={Globe} title="Business Information">
              <div className="space-y-4">
                <label>
                  <FieldLabel>About / Description</FieldLabel>
                  <textarea value={form.about || ''} onChange={e => f('about')(e.target.value)}
                    rows={3} placeholder="Tell customers about your facility…"
                    style={{ ...inputStyle, resize: 'vertical' }} data-testid="sports-settings-about" />
                </label>
                <label>
                  <FieldLabel>Contact Number</FieldLabel>
                  <input value={form.contact || ''} onChange={e => f('contact')(e.target.value)}
                    placeholder="+63 9XX XXX XXXX" style={inputStyle} data-testid="sports-settings-contact" />
                </label>
                <label>
                  <FieldLabel>Location / Address</FieldLabel>
                  <input value={form.location || ''} onChange={e => f('location')(e.target.value)}
                    placeholder="e.g. Pasay City, Metro Manila" style={inputStyle} data-testid="sports-settings-location" />
                </label>
                <label>
                  <FieldLabel>Google Maps Direct Link (optional)</FieldLabel>
                  <input value={form.maps_url || ''} onChange={e => f('maps_url')(e.target.value)}
                    placeholder="https://maps.app.goo.gl/abc123  or  https://www.google.com/maps?q=..."
                    style={inputStyle} data-testid="sports-settings-maps-url" />
                  <p className="text-xs mt-1" style={{ color: '#555' }}>
                    Paste your Google Maps pin link. Customers tap the address to open your exact location.
                    Get it: Google Maps → your location → Share → Copy link.
                  </p>
                </label>
              </div>
            </SectionCard>

            {/* Payment Mode */}
            <SectionCard icon={CreditCard} title="Payment & Booking Mode">
              <div>
                <FieldLabel>How customers pay</FieldLabel>
                <p className="text-xs mb-3" style={{ color: '#555' }}>
                  Choose when payment is collected from customers.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {[
                    { id: 'pay_first', label: 'Pay First (GCash)', desc: 'Customer uploads GCash screenshot before booking is confirmed. Default secure mode.', icon: '📱' },
                    { id: 'pay_on_arrival', label: 'Pay on Arrival', desc: 'Booking confirmed immediately. Customer pays in person at the court using Walk-In POS.', icon: '💵' },
                  ].map(opt => (
                    <button key={opt.id} onClick={() => f('payment_mode')(opt.id)}
                      data-testid={`sports-settings-payment-${opt.id}`}
                      className="p-4 rounded-xl text-left transition-all"
                      style={{
                        background: form.payment_mode === opt.id ? '#E6394614' : '#0A0A0A',
                        border: `1px solid ${form.payment_mode === opt.id ? '#E63946' : '#2A2A2A'}`,
                      }}>
                      <p className="text-xl mb-1">{opt.icon}</p>
                      <p className="text-sm font-bold mb-1" style={{ color: form.payment_mode === opt.id ? '#E63946' : '#fff' }}>{opt.label}</p>
                      <p className="text-xs" style={{ color: '#555', lineHeight: 1.5 }}>{opt.desc}</p>
                    </button>
                  ))}
                </div>
                {form.payment_mode === 'pay_on_arrival' && (
                  <p className="text-xs mt-3 px-3 py-2 rounded-lg" style={{ background: '#FFB30012', color: '#FFB300', border: '1px solid #FFB30030' }}>
                    Customers will receive their QR code immediately. Use the Walk-In POS or Scanner to collect payment at the court.
                  </p>
                )}
              </div>
            </SectionCard>

            {/* Online Payments — per-tenant PayMongo */}
            <SectionCard icon={CreditCard} title="Online Payments (PayMongo)">
              <PayMongoSection settings={settings} form={form} f={f}
                onChanged={() => sportsApi.get('/sports/settings').then(r => setSettings(r.data || {})).catch(() => {})} />
            </SectionCard>

            {/* Booking Rules */}
            <SectionCard icon={Calendar} title="Booking Rules">
              <div>
                <FieldLabel>Advance Booking Limit</FieldLabel>
                <p className="text-xs mb-3" style={{ color: '#555' }}>
                  How far ahead customers can book. Prevents over-booking when you need dates for competitions.
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { value: 7, label: '1 Week' },
                    { value: 14, label: '2 Weeks' },
                    { value: 28, label: '4 Weeks' },
                    { value: null, label: 'Unlimited' },
                  ].map(opt => (
                    <button key={String(opt.value)} onClick={() => f('max_advance_days')(opt.value)}
                      data-testid={`sports-settings-advance-${opt.value || 'unlimited'}`}
                      className="p-3 rounded-xl text-sm font-bold transition-all"
                      style={{
                        background: form.max_advance_days === opt.value ? '#E6394614' : '#0A0A0A',
                        border: `1px solid ${form.max_advance_days === opt.value ? '#E63946' : '#2A2A2A'}`,
                        color: form.max_advance_days === opt.value ? '#E63946' : '#fff',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="mt-6">
                <FieldLabel>Time Zone</FieldLabel>
                <p className="text-xs mb-3" style={{ color: '#555' }}>
                  Your venue's local time zone. Anchors "today", the booking window, and reminder emails to your local clock — so visitors booking from anywhere see your dates.
                </p>
                <select
                  data-testid="sports-settings-timezone"
                  value={form.timezone || 'Asia/Manila'}
                  onChange={e => f('timezone')(e.target.value)}
                  className="w-full p-3 rounded-xl text-sm font-semibold"
                  style={{ background: '#0A0A0A', border: '1px solid #2A2A2A', color: '#fff', cursor: 'pointer' }}>
                  {[
                    ['Asia/Manila', 'Philippines (Manila, GMT+8)'],
                    ['Asia/Singapore', 'Singapore (GMT+8)'],
                    ['Asia/Hong_Kong', 'Hong Kong (GMT+8)'],
                    ['Asia/Kuala_Lumpur', 'Malaysia (Kuala Lumpur, GMT+8)'],
                    ['Asia/Jakarta', 'Indonesia (Jakarta, GMT+7)'],
                    ['Asia/Bangkok', 'Thailand (Bangkok, GMT+7)'],
                    ['Asia/Tokyo', 'Japan (Tokyo, GMT+9)'],
                    ['Asia/Seoul', 'South Korea (Seoul, GMT+9)'],
                    ['Asia/Kolkata', 'India (Kolkata, GMT+5:30)'],
                    ['Asia/Dubai', 'UAE (Dubai, GMT+4)'],
                    ['Australia/Sydney', 'Australia (Sydney, GMT+10/11)'],
                    ['Pacific/Auckland', 'New Zealand (Auckland, GMT+12/13)'],
                    ['Europe/London', 'UK (London, GMT+0/1)'],
                    ['Europe/Paris', 'Central Europe (Paris, GMT+1/2)'],
                    ['America/New_York', 'US Eastern (New York)'],
                    ['America/Chicago', 'US Central (Chicago)'],
                    ['America/Denver', 'US Mountain (Denver)'],
                    ['America/Los_Angeles', 'US Pacific (Los Angeles)'],
                    ['Etc/UTC', 'UTC'],
                  ].map(([tz, label]) => <option key={tz} value={tz}>{label}</option>)}
                </select>
              </div>

              {/* Play Now — instant pro-rated booking of the in-progress slot */}
              <div className="mt-6">
                <FieldLabel>Play Now (Instant In-Progress Booking)</FieldLabel>
                <p className="text-xs mb-3" style={{ color: '#555' }}>
                  Let customers book a court that's already mid-slot. Start is pushed forward by a short buffer so they can settle payment, and the price is pro-rated to the minutes actually played. Shows on both your public page and the Walk-In POS.
                </p>
                <div className="flex items-center gap-3 mb-4">
                  {[
                    { id: true, label: 'Enabled' },
                    { id: false, label: 'Disabled' },
                  ].map(opt => (
                    <button key={String(opt.id)} onClick={() => f('play_now_enabled')(opt.id)}
                      data-testid={`sports-settings-playnow-${opt.id ? 'on' : 'off'}`}
                      className="flex-1 p-3 rounded-xl text-sm font-bold transition-all"
                      style={{
                        background: !!form.play_now_enabled === opt.id ? '#00E67614' : '#0A0A0A',
                        border: `1px solid ${!!form.play_now_enabled === opt.id ? '#00E676' : '#2A2A2A'}`,
                        color: !!form.play_now_enabled === opt.id ? '#00E676' : '#fff',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
                {form.play_now_enabled && (
                  <div>
                    <FieldLabel>Settle Buffer</FieldLabel>
                    <p className="text-xs mb-3" style={{ color: '#555' }}>
                      Lead time before a Play Now booking starts (rounded up to the next 5 minutes).
                    </p>
                    <div className="grid grid-cols-4 gap-3">
                      {[5, 10, 15, 20].map(min => (
                        <button key={min} onClick={() => f('play_now_buffer_min')(min)}
                          data-testid={`sports-settings-playnow-buffer-${min}`}
                          className="p-3 rounded-xl text-sm font-bold transition-all"
                          style={{
                            background: Number(form.play_now_buffer_min) === min ? '#00E67614' : '#0A0A0A',
                            border: `1px solid ${Number(form.play_now_buffer_min) === min ? '#00E676' : '#2A2A2A'}`,
                            color: Number(form.play_now_buffer_min) === min ? '#00E676' : '#fff',
                          }}>
                          {min} min
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Centralized POS — all money through the cashier */}
              <div className="mt-6">
                <FieldLabel>Centralized POS (Cashier Mode)</FieldLabel>
                <p className="text-xs mb-3" style={{ color: '#555' }}>
                  All money runs through the cashier: open play players with an unpaid balance
                  cannot self-activate from their phone — they're told to settle at the front
                  desk first. Staff check-in always works as an override.
                </p>
                <div className="flex items-center gap-3">
                  {[
                    { id: true, label: 'Enabled' },
                    { id: false, label: 'Disabled' },
                  ].map(opt => (
                    <button key={String(opt.id)} onClick={() => f('centralized_pos')(opt.id)}
                      data-testid={`sports-settings-centralpos-${opt.id ? 'on' : 'off'}`}
                      className="flex-1 p-3 rounded-xl text-sm font-bold transition-all"
                      style={{
                        background: !!form.centralized_pos === opt.id ? '#00E67614' : '#0A0A0A',
                        border: `1px solid ${!!form.centralized_pos === opt.id ? '#00E676' : '#2A2A2A'}`,
                        color: !!form.centralized_pos === opt.id ? '#00E676' : '#fff',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Daily money summary email */}
              <div className="mt-6">
                <FieldLabel>Daily Money Summary Email</FieldLabel>
                <p className="text-xs mb-3" style={{ color: '#555' }}>
                  Every morning at 6 AM (your timezone) you get the previous day's money map:
                  booked vs collected vs still owed, split by cash/GCash/online and by who
                  collected it — with a named list of balances to chase. You can also send it
                  any time with "Close the Day" on the dashboard.
                </p>
                <div className="flex items-center gap-3">
                  {[
                    { id: true, label: 'Enabled' },
                    { id: false, label: 'Disabled' },
                  ].map(opt => (
                    <button key={String(opt.id)} onClick={() => f('eod_email_enabled')(opt.id)}
                      data-testid={`sports-settings-eodemail-${opt.id ? 'on' : 'off'}`}
                      className="flex-1 p-3 rounded-xl text-sm font-bold transition-all"
                      style={{
                        background: !!form.eod_email_enabled === opt.id ? '#00E67614' : '#0A0A0A',
                        border: `1px solid ${!!form.eod_email_enabled === opt.id ? '#00E676' : '#2A2A2A'}`,
                        color: !!form.eod_email_enabled === opt.id ? '#00E676' : '#fff',
                      }}>
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </SectionCard>

            {/* Custom Subdomain — MOVED to Site → Your Site Addresses (2026-08-09).
                The permanent /book link + QR generator + web-address claim all
                live there now (components/SiteAddresses.jsx). */}
            <SectionCard icon={Link2} title="Custom Subdomain">
              <p className="text-xs" style={{ color: '#555', lineHeight: 1.6 }}>
                Your booking links are now managed in{' '}
                <a href="/sports/site-builder" style={{ color: '#E63946', fontWeight: 700 }}>Site → Your Site Addresses</a>
                {' '}— the permanent QR link and your custom .agri-books.com web address.
              </p>
            </SectionCard>

            {/* Save */}
            <div className="flex justify-end mt-2 mb-5">
              <button onClick={save} disabled={saving} data-testid="sports-settings-save-btn"
                className="flex items-center gap-2 px-6 py-3 rounded-md font-bold uppercase tracking-wide transition-all"
                style={{ background: '#E63946', color: '#fff', opacity: saving ? 0.7 : 1, boxShadow: '0 0 20px rgba(230,57,70,0.3)' }}>
                <Save className="h-4 w-4" /> {saving ? 'Saving…' : 'Save Settings'}
              </button>
            </div>

            {/* Account security — own Update button, independent of Save Settings above */}
            <ChangePasswordSection />
          </>
        )}
      </main>
    </div>
  );
}
